import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Type,
  Link2,
  Tag,
  Tags as TagsIcon,
  CalendarClock,
  Gauge,
  Lock,
  ChevronDown,
  Eye,
  EyeOff,
  Sparkles,
  X,
  Megaphone,
  Globe,
  Smartphone,
  Shuffle,
  Plus,
  CornerDownRight,
  AlertCircle,
  Hash,
  Check,
  Loader2,
  Trash2,
  UserCheck,
  LayoutTemplate,
  Share2,
  Image as ImageIcon,
  UserPlus,
} from "lucide-react";
import Button from "../../../components/ui/Button";
import Badge from "../../../components/ui/Badge";
import CountrySelect from "../../../components/ui/CountrySelect";
import ImageInput from "../../../components/ui/ImageInput";
import HelpHint from "../../../components/ui/HelpHint";
import {
  Link as LinkType,
  UTMParams,
  Campaign,
  GeoRule,
  DeviceRule,
  DeviceMatch,
  RotationItem,
  Domain,
  LinkTag as LinkTagType,
  UtmPreset,
  LeadCaptureField,
  GhlCustomField,
  LandingPage,
} from "../../../types";
import {
  buildUtmUrl,
  parseUtmFromUrl,
  toLocalInputValue,
} from "../../../utils";
import { countryName, flagEmoji } from "../../../utils/countries";
import { useFeature, UpgradeBadge } from "../../../marketplace/FeatureGate";
import { useMarketplace } from "../../../marketplace/context";
import { Features } from "../../../marketplace/plans";
import { useAppServices } from "../../../hook/services";
import LeadFieldsEditor from "../../../components/LeadFieldsEditor";
import env from "../../../config";

export interface LinkFormPayload {
  _id?: string;
  title: string;
  url: string; // final URL with UTM merged in
  activates_at: string | null; // ISO or null — link goes live at this time
  expires_at: string | null; // ISO or null
  max_clicks?: number | null;
  password?: string; // present = set new password
  clear_password?: boolean; // present = remove password gate
  campaign_id?: string | null;
  geo_rules?: GeoRule[]; // country-targeted destination overrides
  device_rules?: DeviceRule[]; // device/OS destination overrides (Pro)
  rotation?: RotationItem[]; // weighted A/B rotation (Pro)
  slug?: string; // custom short slug ("" clears it on update)
  domain_host?: string; // branded domain ("" clears it on update)
  tags?: string[];
  click_tags?: string[]; // GHL tags applied to the clicking contact
  append_contact_id?: boolean; // append ?contact_id={{contact.id}} to short URL
  sync_contact?: boolean; // write-back to the GHL contact on click
  deep_link?: boolean; // open the destination in its native app
  og_title?: string; // custom social preview
  og_description?: string;
  og_image?: string;
  lead_capture?: {
    enabled: boolean;
    mode: "form" | "page";
    page_id: string;
    fields: LeadCaptureField[];
    headline: string;
    subtext: string;
  };
}

// Tolerate the legacy string[] shape when seeding the builder from a saved link.
const normalizeInitialLeadFields = (
  fields?: (LeadCaptureField | string)[],
): LeadCaptureField[] => {
  if (!Array.isArray(fields) || !fields.length) {
    return [{ key: "name" }, { key: "email", required: true }];
  }
  return fields.map((f) => (typeof f === "string" ? { key: f } : { ...f }));
};

const DEVICE_OPTIONS: { value: DeviceMatch; label: string }[] = [
  { value: "ios", label: "iOS (iPhone/iPad)" },
  { value: "android", label: "Android" },
  { value: "mobile", label: "Any mobile" },
  { value: "tablet", label: "Any tablet" },
  { value: "desktop", label: "Desktop" },
  { value: "windows", label: "Windows" },
  { value: "macos", label: "macOS" },
  { value: "linux", label: "Linux" },
];
const deviceLabel = (m: string) =>
  DEVICE_OPTIONS.find((o) => o.value === m)?.label || m;

interface Props {
  initial?: LinkType | null;
  submitLabel?: string;
  submitting?: boolean;
  error?: string | null;
  onSubmit: (payload: LinkFormPayload) => void;
  onCancel: () => void;
}

const Section = ({
  open,
  setOpen,
  icon,
  label,
  badge,
  feature,
  help,
  children,
}: {
  open: boolean;
  setOpen: (b: boolean) => void;
  icon: React.ReactNode;
  label: string;
  badge?: React.ReactNode;
  feature?: keyof Omit<Features, "maxLinks">;
  help?: { title?: string; anchor?: string; body: React.ReactNode };
  children: React.ReactNode;
}) => {
  const gate = useFeature(feature || ("utm" as any));
  const locked = !!feature && !gate.allowed;
  return (
    <div
      className={`border rounded-xl overflow-hidden ${
        locked ? "border-ink-200/70 bg-ink-50/40" : "border-ink-200/70"
      }`}
    >
      <button
        type="button"
        onClick={() => !locked && setOpen(!open)}
        disabled={locked}
        className={`w-full flex items-center justify-between px-3.5 h-11 ${
          locked
            ? "bg-ink-50 text-ink-400 cursor-not-allowed"
            : "bg-white hover:bg-ink-50/60 transition-colors"
        }`}
      >
        <div className="flex items-center gap-2.5">
          <span className={locked ? "text-ink-300" : "text-ink-500"}>
            {locked ? <Lock size={13} /> : icon}
          </span>
          <span
            className={`text-sm font-medium ${
              locked ? "text-ink-500" : "text-ink-700"
            }`}
          >
            {label}
          </span>
          {help && (
            <HelpHint title={help.title || label} anchor={help.anchor}>
              {help.body}
            </HelpHint>
          )}
          {locked ? (
            <UpgradeBadge feature={feature as any} />
          ) : (
            badge
          )}
        </div>
        {!locked && (
          <ChevronDown
            size={14}
            className={`text-ink-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && !locked && (
        <div className="border-t border-ink-100 bg-ink-50/30 p-3.5">
          {children}
        </div>
      )}
    </div>
  );
};

// A titled group of related fields — gives the form clear, scannable sections.
const Group = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-3 pt-1">
    <div className="border-b border-ink-100 pb-1.5">
      <h3 className="text-sm font-semibold text-ink-900 tracking-tight">
        {title}
      </h3>
      {description && (
        <p className="text-[11px] text-ink-500 mt-0.5">{description}</p>
      )}
    </div>
    {children}
  </section>
);

const UTM_FIELDS: { key: keyof UTMParams; label: string; hint: string }[] = [
  { key: "source", label: "Source", hint: "newsletter, twitter" },
  { key: "medium", label: "Medium", hint: "email, cpc, social" },
  { key: "campaign", label: "Campaign", hint: "summer_sale_2026" },
  { key: "term", label: "Term", hint: "paid keyword" },
  { key: "content", label: "Content", hint: "banner_top" },
];

const LinkForm = ({
  initial,
  submitLabel = "Create link",
  submitting,
  error,
  onSubmit,
  onCancel,
}: Props) => {
  const { features, customDomain, account } = useMarketplace();
  const services = useAppServices();
  const campaignsFeat = useFeature("campaigns");
  const slugFeat = useFeature("customSlugs");
  const domainFeat = useFeature("customDomain");
  const geoFeat = useFeature("geoTargeting");
  const deviceFeat = useFeature("deviceTargeting");
  const abFeat = useFeature("abTesting");
  const parsed = useMemo(
    () => parseUtmFromUrl(initial?.url || ""),
    [initial],
  );

  const [title, setTitle] = useState(initial?.title || "");
  const [url, setUrl] = useState(parsed.base);
  const [utm, setUtm] = useState<UTMParams>(parsed.utm);
  const [activatesAt, setActivatesAt] = useState<string>(
    toLocalInputValue(initial?.activates_at),
  );
  const [expiresAt, setExpiresAt] = useState<string>(
    toLocalInputValue(initial?.expires_at),
  );
  const [maxClicks, setMaxClicks] = useState(
    initial?.max_clicks != null ? String(initial.max_clicks) : "",
  );
  const [tags, setTags] = useState<string[]>(initial?.tags || []);
  const [managedTags, setManagedTags] = useState<LinkTagType[]>([]);
  const [utmPresets, setUtmPresets] = useState<UtmPreset[]>([]);
  const [presetName, setPresetName] = useState("");
  const [showSavePreset, setShowSavePreset] = useState(false);
  // GHL tag-on-click
  const [clickTags, setClickTags] = useState<string[]>(initial?.click_tags || []);
  const [clickTagInput, setClickTagInput] = useState("");
  const [appendContactId, setAppendContactId] = useState(
    !!initial?.append_contact_id,
  );
  const [syncContact, setSyncContact] = useState(!!initial?.sync_contact);
  const [deepLink, setDeepLink] = useState(!!initial?.deep_link);
  // Custom social preview (Open Graph)
  const [ogTitle, setOgTitle] = useState(initial?.og_title || "");
  const [ogDescription, setOgDescription] = useState(
    initial?.og_description || "",
  );
  const [ogImage, setOgImage] = useState(initial?.og_image || "");
  // Lead capture
  const [leadEnabled, setLeadEnabled] = useState(
    !!initial?.lead_capture?.enabled,
  );
  const [leadMode, setLeadMode] = useState<"form" | "page">(
    initial?.lead_capture?.mode === "page" ? "page" : "form",
  );
  const [leadPageId, setLeadPageId] = useState(
    initial?.lead_capture?.page_id || "",
  );
  const [leadPages, setLeadPages] = useState<LandingPage[]>([]);
  const [leadFields, setLeadFields] = useState<LeadCaptureField[]>(
    normalizeInitialLeadFields(initial?.lead_capture?.fields),
  );
  const [customFields, setCustomFields] = useState<GhlCustomField[]>([]);
  const [leadHeadline, setLeadHeadline] = useState(
    initial?.lead_capture?.headline || "",
  );
  const [leadSubtext, setLeadSubtext] = useState(
    initial?.lead_capture?.subtext || "",
  );
  // Destination: a custom URL OR a GHL funnel/website page (one or the other).
  const [destType, setDestType] = useState<"url" | "funnel">("url");
  const [funnels, setFunnels] = useState<any[]>([]);
  const [funnelId, setFunnelId] = useState("");
  const [funnelPages, setFunnelPages] = useState<any[]>([]);
  const [selectedPageId, setSelectedPageId] = useState("");
  // Domain the funnel/website page preview URL is built on. Defaults to GHL's
  // shared host; whitelabel accounts can pick their own domain.
  const [pageDomain, setPageDomain] = useState("");
  const [funnelsLoading, setFunnelsLoading] = useState(false);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [deviceRules, setDeviceRules] = useState<DeviceRule[]>(initial?.device_rules || []);
  const [deviceMatch, setDeviceMatch] = useState<DeviceMatch>("ios");
  const [deviceUrl, setDeviceUrl] = useState("");
  const [rotation, setRotation] = useState<RotationItem[]>(initial?.rotation || []);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [clearPassword, setClearPassword] = useState(false);
  const [campaignId, setCampaignId] = useState<string>(
    initial?.campaign_id || "",
  );
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [geoRules, setGeoRules] = useState<GeoRule[]>(
    initial?.geo_rules || [],
  );
  const [ruleCountry, setRuleCountry] = useState("");
  const [ruleUrl, setRuleUrl] = useState("");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [slugStatus, setSlugStatus] = useState<{
    state: "idle" | "checking" | "ok" | "unavailable";
    reason?: string;
  }>({ state: "idle" });
  const [domainHost, setDomainHost] = useState(
    // New links default to the account's default branded domain (if any);
    // editing keeps whatever the link already has ("" = the plain app domain).
    initial ? initial.domain_host || "" : customDomain || "",
  );
  const [domains, setDomains] = useState<Domain[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(false);

  const [utmOpen, setUtmOpen] = useState(Object.values(parsed.utm).some(Boolean));
  const [tagsOpen, setTagsOpen] = useState((initial?.tags?.length || 0) > 0);
  const [clickTagsOpen, setClickTagsOpen] = useState(
    (initial?.click_tags?.length || 0) > 0 ||
      !!initial?.append_contact_id ||
      !!initial?.sync_contact,
  );
  const [expOpen, setExpOpen] = useState(
    !!initial?.activates_at || !!initial?.expires_at || initial?.max_clicks != null,
  );
  const [deviceOpen, setDeviceOpen] = useState((initial?.device_rules?.length || 0) > 0);
  const [rotationOpen, setRotationOpen] = useState((initial?.rotation?.length || 0) > 0);
  const [pwOpen, setPwOpen] = useState(!!initial?.has_password);
  const [campOpen, setCampOpen] = useState(!!initial?.campaign_id);
  const [geoOpen, setGeoOpen] = useState(
    (initial?.geo_rules?.length || 0) > 0,
  );
  const [slugOpen, setSlugOpen] = useState(!!initial?.slug);
  const [domainOpen, setDomainOpen] = useState(!!initial?.domain_host);
  const [socialOpen, setSocialOpen] = useState(
    !!(initial?.og_title || initial?.og_description || initial?.og_image),
  );
  const [leadOpen, setLeadOpen] = useState(!!initial?.lead_capture?.enabled);

  // Load campaigns for the selector when the feature is unlocked.
  useEffect(() => {
    if (!campaignsFeat.allowed) return;
    let cancelled = false;
    (async () => {
      const { response } = await services.campaigns.get();
      if (!cancelled && Array.isArray(response)) setCampaigns(response);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignsFeat.allowed]);

  // Load the account's managed tags, saved UTM presets, and GHL custom fields.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [tagsRes, presetsRes, cfRes, pagesRes] = await Promise.all([
        services.link_tags.get(),
        services.utm_presets.get(),
        services.ghl.custom_fields(),
        services.landing_pages.get(),
      ]);
      if (!cancelled && Array.isArray(tagsRes.response))
        setManagedTags(tagsRes.response);
      if (!cancelled && Array.isArray(presetsRes.response))
        setUtmPresets(presetsRes.response);
      if (!cancelled && Array.isArray(cfRes.response))
        setCustomFields(cfRes.response);
      if (!cancelled && Array.isArray(pagesRes.response))
        setLeadPages(pagesRes.response);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyUtmPreset = (p: UtmPreset) =>
    setUtm({
      source: p.source || "",
      medium: p.medium || "",
      campaign: p.campaign || "",
      term: p.term || "",
      content: p.content || "",
    });

  const saveUtmPreset = async () => {
    const name = presetName.trim();
    if (!name) return;
    const { response } = await services.utm_presets.create({
      payload: { name, ...utm },
    });
    if (response) {
      setUtmPresets((cur) =>
        [...cur.filter((x) => x._id !== response._id), response].sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      );
      setPresetName("");
      setShowSavePreset(false);
    }
  };

  // Load the account's verified branded domains for the picker.
  useEffect(() => {
    if (!domainFeat.allowed) return;
    let cancelled = false;
    setDomainsLoading(true);
    (async () => {
      const { response } = await services.url_domains.get();
      if (cancelled) return;
      if (Array.isArray(response)) {
        setDomains((response as Domain[]).filter((d) => d.verified));
      }
      setDomainsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainFeat.allowed]);

  // Load GHL funnels when the user switches the destination to "funnel".
  useEffect(() => {
    if (destType !== "funnel" || funnels.length) return;
    let cancelled = false;
    setFunnelsLoading(true);
    (async () => {
      const { response } = await services.ghl.funnels();
      if (!cancelled && Array.isArray(response)) setFunnels(response);
      if (!cancelled) setFunnelsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destType]);

  // Load the chosen funnel's pages.
  useEffect(() => {
    setSelectedPageId("");
    if (!funnelId) {
      setFunnelPages([]);
      return;
    }
    let cancelled = false;
    setPagesLoading(true);
    (async () => {
      const { response } = await services.ghl.funnels({
        query: `funnelId=${encodeURIComponent(funnelId)}`,
      });
      if (!cancelled && Array.isArray(response)) setFunnelPages(response);
      if (!cancelled) setPagesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnelId]);

  useEffect(() => {
    if (!initial) return;
    setTitle(initial.title || "");
    const p = parseUtmFromUrl(initial.url || "");
    setUrl(p.base);
    setUtm(p.utm);
    setActivatesAt(toLocalInputValue(initial.activates_at));
    setExpiresAt(toLocalInputValue(initial.expires_at));
    setMaxClicks(initial.max_clicks != null ? String(initial.max_clicks) : "");
    setTags(initial.tags || []);
    setClickTags(initial.click_tags || []);
    setClickTagInput("");
    setAppendContactId(!!initial.append_contact_id);
    setSyncContact(!!initial.sync_contact);
    setDeepLink(!!initial.deep_link);
    setOgTitle(initial.og_title || "");
    setOgDescription(initial.og_description || "");
    setOgImage(initial.og_image || "");
    setLeadEnabled(!!initial.lead_capture?.enabled);
    setLeadMode(initial.lead_capture?.mode === "page" ? "page" : "form");
    setLeadPageId(initial.lead_capture?.page_id || "");
    setLeadFields(normalizeInitialLeadFields(initial.lead_capture?.fields));
    setLeadHeadline(initial.lead_capture?.headline || "");
    setLeadSubtext(initial.lead_capture?.subtext || "");
    setDeviceRules(initial.device_rules || []);
    setRotation(initial.rotation || []);
    setClearPassword(false);
    setPassword("");
    setCampaignId(initial.campaign_id || "");
    setGeoRules(initial.geo_rules || []);
    setSlug(initial.slug || "");
    setSlugStatus({ state: "idle" });
    setDomainHost(initial.domain_host || "");
  }, [initial]);

  // Debounced slug availability check (advisory — the server re-checks on save).
  useEffect(() => {
    const s = slug.trim().toLowerCase();
    if (!slugFeat.allowed) return;
    // Empty, or unchanged from the link's existing slug → nothing to check.
    if (!s || s === (initial?.slug || "")) {
      setSlugStatus({ state: "idle" });
      return;
    }
    if (s.length < 3) {
      setSlugStatus({ state: "unavailable", reason: "At least 3 characters." });
      return;
    }
    setSlugStatus({ state: "checking" });
    const t = setTimeout(async () => {
      const q = `slug=${encodeURIComponent(s)}${
        initial?._id ? `&_id=${initial._id}` : ""
      }`;
      const { response } = await services.url_tracker.slugAvailable({ query: q });
      if (response && response.available) {
        setSlugStatus({ state: "ok" });
      } else {
        setSlugStatus({
          state: "unavailable",
          reason: response?.reason || "That slug is taken.",
        });
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, slugFeat.allowed, initial]);

  const hasUtm = Object.values(utm).some((v) => v && v.length > 0);
  const utmPreview = useMemo(() => buildUtmUrl(url, utm), [url, utm]);

  const addGeoRule = () => {
    const code = ruleCountry.trim().toUpperCase();
    const dest = ruleUrl.trim();
    if (!code || !dest) return;
    setGeoRules((rules) => [
      // One rule per country — replace any existing rule for this code.
      ...rules.filter((r) => r.country_code !== code),
      { country_code: code, country: countryName(code), url: dest },
    ]);
    setRuleCountry("");
    setRuleUrl("");
  };

  const removeGeoRule = (code: string) =>
    setGeoRules((rules) => rules.filter((r) => r.country_code !== code));

  const alreadyTargeted =
    !!ruleCountry &&
    geoRules.some((r) => r.country_code === ruleCountry.toUpperCase());

  // Tags are picked from the account's managed list only.
  const toggleTag = (name: string) =>
    setTags((curr) =>
      curr.includes(name) ? curr.filter((x) => x !== name) : [...curr, name],
    );

  const addClickTag = (raw: string) => {
    const t = raw.trim().toLowerCase();
    if (!t) return;
    setClickTags((curr) =>
      curr.includes(t) || curr.length >= 20 ? curr : [...curr, t],
    );
    setClickTagInput("");
  };
  const removeClickTag = (t: string) =>
    setClickTags((curr) => curr.filter((x) => x !== t));

  const addDeviceRule = () => {
    const dest = deviceUrl.trim();
    if (!dest) return;
    setDeviceRules((rules) => [
      ...rules.filter((r) => r.match !== deviceMatch),
      { match: deviceMatch, url: dest },
    ]);
    setDeviceUrl("");
  };
  const removeDeviceRule = (match: string) =>
    setDeviceRules((rules) => rules.filter((r) => r.match !== match));

  const addRotation = () =>
    setRotation((r) => (r.length >= 10 ? r : [...r, { url: "", weight: 1 }]));
  const updateRotation = (i: number, patch: Partial<RotationItem>) =>
    setRotation((r) => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeRotation = (i: number) =>
    setRotation((r) => r.filter((_, idx) => idx !== i));
  const rotationTotal = rotation.reduce((s, r) => s + (r.weight > 0 ? r.weight : 1), 0);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const mc = parseInt(maxClicks, 10);
    const payload: LinkFormPayload = {
      _id: initial?._id,
      title: title.trim(),
      url: hasUtm ? utmPreview : url.trim(),
      activates_at: activatesAt ? new Date(activatesAt).toISOString() : null,
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      max_clicks: Number.isFinite(mc) && mc > 0 ? mc : null,
      campaign_id: campaignsFeat.allowed ? campaignId || "" : undefined,
      // Only send geo rules when unlocked, so a locked user's edit doesn't
      // touch (or get rejected for) the link's existing rules.
      geo_rules: geoFeat.allowed ? geoRules : undefined,
      device_rules: deviceFeat.allowed ? deviceRules : undefined,
      rotation: abFeat.allowed ? rotation.filter((r) => r.url.trim()) : undefined,
      // Only send slug if the feature is unlocked. "" clears it on update.
      slug: slugFeat.allowed ? slug.trim().toLowerCase() : undefined,
      // Only send domain if the feature is unlocked. "" clears it on update.
      domain_host: domainFeat.allowed ? domainHost : undefined,
      tags,
      click_tags: clickTags,
      append_contact_id: appendContactId,
      sync_contact: syncContact,
      deep_link: deepLink,
      og_title: ogTitle.trim(),
      og_description: ogDescription.trim(),
      og_image: ogImage.trim(),
      lead_capture: {
        enabled: leadEnabled,
        mode: leadMode,
        page_id: leadMode === "page" ? leadPageId : "",
        fields: leadFields.map((f) => ({
          key: f.key,
          label: (f.label || "").trim() || undefined,
          required: !!f.required,
          custom: !!f.custom,
          dataType: f.dataType || undefined,
        })),
        headline: leadHeadline.trim(),
        subtext: leadSubtext.trim(),
      },
    };
    if (password) payload.password = password;
    else if (clearPassword) payload.clear_password = true;
    onSubmit(payload);
  };

  const selectedCampaign = campaigns.find((c) => c._id === campaignId);

  // GHL page preview URLs are <domain>/preview/<pageId>. The host is GHL's
  // shared sites domain by default; whitelabel accounts can swap in their own.
  const STATIC_PAGE_DOMAIN = "sites.leadconnectorhq.com";
  const isWhitelabel = !!account?.is_whitelabel && !!account?.domain;
  const pageDomainOptions = isWhitelabel
    ? [account!.domain as string, STATIC_PAGE_DOMAIN]
    : [STATIC_PAGE_DOMAIN];
  const effectivePageDomain = pageDomain || pageDomainOptions[0];

  const buildPageUrl = (p: any, domain = effectivePageDomain): string => {
    const id = p?._id || p?.id;
    if (!id) return "";
    const host = String(domain).replace(/^https?:\/\//, "").replace(/\/+$/, "");
    return `https://${host}/preview/${id}`;
  };

  // The plain app host (no branded domain).
  const appHost = env.REDIRECT_BASE.replace(/^https?:\/\//, "");
  // Host shown in the slug prefix + short-URL preview: the picked branded
  // domain, or the app host when "App domain" is selected.
  const shortHost = domainHost
    ? domainHost.replace(/^https?:\/\//, "")
    : appHost;
  const slugTrimmed = slug.trim().toLowerCase();
  const slugBlocksSubmit =
    slugFeat.allowed && slugStatus.state === "unavailable";
  // In funnel mode the destination only exists once a page resolves a URL.
  const destBlocksSubmit = destType === "funnel" && !url.trim();

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {!initial && Number.isFinite(features.dataRetentionDays) && (
        <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200/70 text-[12px] text-amber-900">
          <CalendarClock size={14} className="text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">
              You'll see {features.dataRetentionDays} days of click & scan data
            </div>
            <div className="text-amber-800/80 mt-0.5">
              The link stays live forever — only analytics older than{" "}
              {features.dataRetentionDays} days are trimmed. Upgrade to Pro for
              unlimited history.
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      <div>
        <label className="flex items-center gap-1.5 text-xs font-medium text-ink-700 mb-1.5">
          Title
          <HelpHint title="Title" anchor="field-title">
            An internal name so you can recognise this link in your dashboard and
            reports. It's never shown to your visitors.
          </HelpHint>
        </label>
        <div className="relative">
          <Type
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Summer launch campaign"
            required
            className="w-full pl-9 pr-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
          />
        </div>
      </div>

      {/* Destination — a custom URL OR a GHL funnel/website page */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="flex items-center gap-1.5 text-xs font-medium text-ink-700">
            Destination
            <HelpHint title="Destination" anchor="field-destination">
              Where visitors land by default — a custom URL or one of your CRM
              funnel / website pages. The routing rules further down can override
              this for specific visitors.
            </HelpHint>
          </label>
          <div className="inline-flex p-0.5 bg-ink-100 rounded-lg">
            <button
              type="button"
              onClick={() => setDestType("url")}
              className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-medium transition-all ${
                destType === "url"
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-500 hover:text-ink-900"
              }`}
            >
              <Link2 size={12} /> Custom URL
            </button>
            <button
              type="button"
              onClick={() => setDestType("funnel")}
              className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-medium transition-all ${
                destType === "funnel"
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-500 hover:text-ink-900"
              }`}
            >
              <LayoutTemplate size={12} /> CRM funnel / website
            </button>
          </div>
        </div>

        {destType === "url" ? (
          <div className="relative">
            <Link2
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/landing"
              required
              className="w-full pl-9 pr-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400 font-mono"
            />
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[11px] text-ink-500">
              Send this link to one of your CRM funnel or website pages.
            </p>
            {funnelsLoading ? (
              <div className="flex items-center gap-2 px-3 h-10 bg-white border border-ink-200 rounded-lg text-sm text-ink-400">
                <Loader2 size={14} className="animate-spin text-ink-400" />
                Loading your funnels…
              </div>
            ) : funnels.length === 0 ? (
              <p className="text-xs text-ink-400">
                No funnels or websites found — make sure the CRM account is
                connected.
              </p>
            ) : (
              <>
                <select
                  value={funnelId}
                  onChange={(e) => setFunnelId(e.target.value)}
                  className="w-full px-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                >
                  <option value="">Select a funnel / website…</option>
                  {funnels.map((f) => (
                    <option key={f._id || f.id} value={f._id || f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                {funnelId &&
                  (pagesLoading ? (
                    <div className="flex items-center gap-2 px-3 h-10 bg-white border border-ink-200 rounded-lg text-sm text-ink-400">
                      <Loader2 size={14} className="animate-spin text-ink-400" />
                      Loading pages…
                    </div>
                  ) : funnelPages.length === 0 ? (
                    <p className="text-xs text-ink-400">No pages in this funnel.</p>
                  ) : (
                    <select
                      value={selectedPageId}
                      onChange={(e) => {
                        setSelectedPageId(e.target.value);
                        const p = funnelPages.find(
                          (pg) => (pg._id || pg.id) === e.target.value,
                        );
                        setUrl(buildPageUrl(p));
                      }}
                      className="w-full px-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                    >
                      <option value="">Select a page…</option>
                      {funnelPages.map((p) => (
                        <option key={p._id || p.id} value={p._id || p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  ))}
                {selectedPageId && isWhitelabel && (
                  <div>
                    <label className="block text-[11px] text-ink-600 mb-1">
                      Page domain
                    </label>
                    <select
                      value={effectivePageDomain}
                      onChange={(e) => {
                        const d = e.target.value;
                        setPageDomain(d);
                        const p = funnelPages.find(
                          (pg) => (pg._id || pg.id) === selectedPageId,
                        );
                        if (p) setUrl(buildPageUrl(p, d));
                      }}
                      className="w-full px-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                    >
                      {pageDomainOptions.map((d) => (
                        <option key={d} value={d}>
                          {d === STATIC_PAGE_DOMAIN ? `${d} (default)` : d}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {selectedPageId && (
                  <div>
                    <label className="block text-[11px] text-ink-600 mb-1">
                      Page URL{" "}
                      <span className="text-ink-400">
                        (auto-filled — edit if needed)
                      </span>
                    </label>
                    <div className="relative">
                      <Link2
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                      />
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://yourdomain.com/page"
                        className="w-full pl-9 pr-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400 font-mono"
                      />
                    </div>
                    {!url && (
                      <p className="mt-1 text-[11px] text-amber-700">
                        Couldn't auto-detect this page's public URL — paste it
                        here to continue.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {hasUtm && (
          <div className="mt-2 text-[11px] text-ink-500 break-all">
            <span className="font-medium text-ink-600">Final URL:</span>{" "}
            <span className="font-mono">{utmPreview}</span>
          </div>
        )}
      </div>

      {/* Short link */}
      <Group title="Short link" description="How your short URL looks.">
      {/* Branded domain */}
      <Section
        open={domainOpen}
        setOpen={setDomainOpen}
        icon={<Link2 size={14} />}
        label="Branded domain"
        feature="customDomain"
        help={{
          anchor: "field-domain",
          body: "Build this link on one of your verified custom domains (e.g. go.yourbrand.com) instead of the default host, so the short URL carries your brand.",
        }}
        badge={
          domainHost ? (
            <Badge tone="brand" className="ml-1">
              {domainHost}
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-2.5">
          Choose which of your verified domains this link's short URL is built
          on. Add or verify domains on the Domains page.
        </p>
        {domainsLoading ? (
          <div className="flex items-center gap-2 px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm text-ink-400">
            <Loader2 size={14} className="animate-spin text-ink-400" />
            Loading your domains…
          </div>
        ) : domains.length === 0 ? (
          <p className="text-xs text-ink-400">
            No verified domains yet — add one and click Verify on the Domains
            page, then it'll show up here.
          </p>
        ) : (
          <select
            value={domainHost}
            onChange={(e) => setDomainHost(e.target.value)}
            className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 font-mono"
          >
            {domains.map((d) => (
              <option key={d._id} value={d.host}>
                {d.host}
                {d.is_default ? " — default" : ""}
              </option>
            ))}
            <option value="">App domain ({appHost})</option>
          </select>
        )}
      </Section>

      {/* Custom slug */}
      <Section
        open={slugOpen}
        setOpen={setSlugOpen}
        icon={<Hash size={14} />}
        label="Custom short link"
        help={{
          anchor: "field-slug",
          body: "Replace the random code with a readable ending like spring-sale. It must be unique across all Smart Links.",
        }}
        feature="customSlugs"
        badge={
          slug.trim() ? (
            <Badge tone="brand" className="ml-1">
              /r/{slugTrimmed}
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-2.5">
          Replace the random short code with your own memorable one. Must be
          unique — lowercase letters, numbers and hyphens only.
        </p>

        <div
          className={`flex items-stretch rounded-lg border bg-white overflow-hidden transition-colors focus-within:ring-2 focus-within:ring-brand-500/20 ${
            slugStatus.state === "unavailable"
              ? "border-rose-300 focus-within:border-rose-400"
              : slugStatus.state === "ok"
              ? "border-emerald-300 focus-within:border-emerald-400"
              : "border-ink-200 focus-within:border-brand-500"
          }`}
        >
          <span className="flex items-center px-2.5 text-[12px] text-ink-400 bg-ink-50/70 border-r border-ink-200 font-mono whitespace-nowrap max-w-[45%] truncate">
            {shortHost}/r/
          </span>
          <input
            type="text"
            value={slug}
            onChange={(e) =>
              setSlug(
                e.target.value
                  .toLowerCase()
                  .replace(/\s+/g, "-")
                  .replace(/[^a-z0-9-]/g, ""),
              )
            }
            placeholder="summer-sale"
            spellCheck={false}
            autoCapitalize="none"
            className="flex-1 min-w-0 px-2.5 h-9 text-sm focus:outline-none placeholder:text-ink-400 font-mono"
          />
          <span className="flex items-center px-2.5">
            {slugStatus.state === "checking" && (
              <Loader2 size={14} className="text-ink-400 animate-spin" />
            )}
            {slugStatus.state === "ok" && (
              <Check size={14} className="text-emerald-600" />
            )}
            {slugStatus.state === "unavailable" && (
              <X size={14} className="text-rose-500" />
            )}
          </span>
        </div>

        {slugStatus.state === "unavailable" && slugStatus.reason && (
          <p className="mt-1.5 text-[11px] text-rose-600 flex items-center gap-1">
            <AlertCircle size={11} className="shrink-0" />
            {slugStatus.reason}
          </p>
        )}
        {slugStatus.state === "ok" && (
          <p className="mt-1.5 text-[11px] text-emerald-600 flex items-center gap-1">
            <Check size={11} className="shrink-0" />
            That short link is available.
          </p>
        )}
        {slugTrimmed && (
          <div className="mt-2 text-[11px] text-ink-500 break-all">
            <span className="font-medium text-ink-600">Short link:</span>{" "}
            <span className="font-mono">
              {shortHost}/r/{slugTrimmed}
            </span>
          </div>
        )}
      </Section>

      {/* Social preview (Open Graph) */}
      <Section
        open={socialOpen}
        setOpen={setSocialOpen}
        icon={<Share2 size={14} />}
        label="Social preview"
        help={{
          anchor: "field-preview",
          body: "Set the title, description and image shown as a card when the link is shared on social or messaging apps. Overrides the destination page's own preview.",
        }}
        badge={
          ogTitle || ogDescription || ogImage ? (
            <Badge tone="brand" className="ml-1">
              Custom
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-2.5">
          Control the card that appears when this link is shared in WhatsApp,
          Facebook, iMessage, LinkedIn, etc. Leave blank to use the
          destination's own preview.
        </p>

        <label className="block text-[11px] text-ink-600 mb-1">Title</label>
        <input
          type="text"
          value={ogTitle}
          onChange={(e) => setOgTitle(e.target.value)}
          maxLength={140}
          placeholder={title || "Your headline"}
          className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
        />

        <label className="block text-[11px] text-ink-600 mb-1 mt-3">
          Description
        </label>
        <textarea
          value={ogDescription}
          onChange={(e) => setOgDescription(e.target.value)}
          maxLength={300}
          rows={2}
          placeholder="A short description shown under the title"
          className="w-full px-3 py-2 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400 resize-none"
        />

        <label className="block text-[11px] text-ink-600 mb-1 mt-3">
          Image{" "}
          <span className="text-ink-400">(1200×630 recommended)</span>
        </label>
        <ImageInput value={ogImage} onChange={setOgImage} />

        {/* Live unfurl preview */}
        <div className="mt-3">
          <div className="text-[11px] text-ink-500 mb-1.5">Preview</div>
          <div className="max-w-sm rounded-xl overflow-hidden border border-ink-200 bg-white">
            {ogImage ? (
              <div className="aspect-[1.91/1] bg-ink-100">
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <img
                  src={ogImage}
                  alt="preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : (
              <div className="aspect-[1.91/1] bg-ink-100 flex items-center justify-center text-ink-300">
                <ImageIcon size={24} />
              </div>
            )}
            <div className="px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-ink-400 truncate">
                {shortHost}
              </div>
              <div className="text-sm font-semibold text-ink-900 truncate">
                {ogTitle || title || "Your title here"}
              </div>
              {ogDescription && (
                <div className="text-xs text-ink-500 line-clamp-2">
                  {ogDescription}
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>
      </Group>

      {/* Tracking */}
      <Group title="Tracking" description="Attribute clicks in your analytics tools.">
      {/* UTM */}
      <Section
        open={utmOpen}
        setOpen={setUtmOpen}
        icon={<Tag size={14} />}
        label="UTM tags"
        help={{
          anchor: "field-utm",
          body: "Adds UTM parameters (source, medium, campaign and more) to the destination URL so analytics tools like Google Analytics can attribute the visit to this link.",
        }}
        feature="utm"
        badge={
          hasUtm ? (
            <Badge tone="brand" className="ml-1">
              Active
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-2.5">
          Track campaign performance in your analytics tool — these are
          appended to the destination URL.
        </p>

        {/* Saved UTM presets — load an existing set or save the current one. */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {utmPresets.length > 0 && (
            <select
              value=""
              onChange={(e) => {
                const p = utmPresets.find((x) => x._id === e.target.value);
                if (p) applyUtmPreset(p);
              }}
              className="h-8 px-2 bg-white border border-ink-200 rounded-md text-xs text-ink-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            >
              <option value="">Load a preset…</option>
              {utmPresets.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          {showSavePreset ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveUtmPreset();
                  } else if (e.key === "Escape") {
                    setShowSavePreset(false);
                    setPresetName("");
                  }
                }}
                placeholder="Preset name"
                className="h-8 w-32 px-2 bg-white border border-ink-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={saveUtmPreset}
                disabled={!presetName.trim()}
              >
                Save
              </Button>
              <button
                type="button"
                onClick={() => {
                  setShowSavePreset(false);
                  setPresetName("");
                }}
                className="w-7 h-7 rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-100 flex items-center justify-center"
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowSavePreset(true)}
              disabled={!hasUtm}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-medium text-ink-500 hover:text-brand-600 hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink-500"
              title={hasUtm ? "Save these UTMs as a preset" : "Fill in some UTM fields first"}
            >
              <Plus size={12} /> Save as preset
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {UTM_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-[11px] text-ink-600 mb-1">
                {f.label}
              </label>
              <input
                type="text"
                value={utm[f.key] || ""}
                onChange={(e) =>
                  setUtm((s) => ({ ...s, [f.key]: e.target.value }))
                }
                placeholder={f.hint}
                className="w-full px-2.5 h-8 bg-white border border-ink-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
              />
            </div>
          ))}
        </div>
      </Section>
      </Group>

      {/* Expiration & limits */}
      <Group title="Expiration & limits" description="Auto-expire or cap how many times the link works.">
      {/* Expiration & click limit */}
      <Section
        open={expOpen}
        setOpen={setExpOpen}
        icon={<CalendarClock size={14} />}
        label="Schedule & limits"
        help={{
          anchor: "field-expiry",
          body: "Schedule when the link goes live, make it stop after a date, and/or cap it at a number of clicks. Outside that window visitors see a friendly page.",
        }}
        feature="expiration"
        badge={
          activatesAt || expiresAt || maxClicks ? (
            <Badge tone="amber" className="ml-1">
              Set
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <label className="block text-[11px] text-ink-600 mb-1">
          Goes live on{" "}
          <span className="text-ink-400">(optional — defaults to now)</span>
          <HelpHint title="Scheduled activation" anchor="field-activation">
            Keep the link dormant until this date/time — visitors who arrive
            early see a "not live yet" page. Great for pre-scheduling launches
            and drops.
          </HelpHint>
        </label>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="datetime-local"
            value={activatesAt}
            onChange={(e) => setActivatesAt(e.target.value)}
            className="flex-1 px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
          {activatesAt && (
            <button
              type="button"
              onClick={() => setActivatesAt("")}
              className="w-9 h-9 rounded-lg border border-ink-200 hover:bg-ink-100 text-ink-500 hover:text-ink-900 flex items-center justify-center"
              title="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <label className="block text-[11px] text-ink-600 mb-1">Expires on</label>
        <div className="flex items-center gap-2">
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="flex-1 px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
          {expiresAt && (
            <button
              type="button"
              onClick={() => setExpiresAt("")}
              className="w-9 h-9 rounded-lg border border-ink-200 hover:bg-ink-100 text-ink-500 hover:text-ink-900 flex items-center justify-center"
              title="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <label className="block text-[11px] text-ink-600 mb-1 mt-3">Maximum clicks</label>
        <div className="relative">
          <Gauge size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            type="number"
            min={1}
            value={maxClicks}
            onChange={(e) => setMaxClicks(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="Unlimited"
            className="w-full pl-9 pr-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
          />
        </div>
        <p className="text-[11px] text-ink-400 mt-1.5">
          The link stops working once it reaches this many clicks. Leave blank for unlimited.
        </p>
      </Section>
      </Group>

      {/* Tags & CRM contact tracking */}
      <Group title="Tags & CRM contact tracking" description="Organize this link, and tag the contact on click.">
      {/* Tags */}
      <Section
        open={tagsOpen}
        setOpen={setTagsOpen}
        icon={<TagsIcon size={14} />}
        label="Tags"
        help={{
          anchor: "tags",
          body: "Internal labels to group and filter your links. Manage the tag set (rename, recolor, delete) from the Manage button on the Links page.",
        }}
        badge={
          tags.length > 0 ? (
            <Badge tone="brand" className="ml-1">{tags.length}</Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-2.5">
          Pick from your tags to group and filter links. Manage tags from the
          Links page (the + next to the filter tabs).
        </p>
        {managedTags.length === 0 ? (
          <p className="text-xs text-ink-400">
            No tags yet — add some on the Links page first.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {managedTags.map((t) => {
              const on = tags.includes(t.name);
              return (
                <button
                  key={t._id}
                  type="button"
                  onClick={() => toggleTag(t.name)}
                  className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-medium transition-all ${
                    on
                      ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200/60"
                      : "bg-white text-ink-500 ring-1 ring-ink-200 hover:text-ink-900"
                  }`}
                >
                  {on && <Check size={11} />}
                  {t.name}
                </button>
              );
            })}
          </div>
        )}
      </Section>

      {/* Tag contact on click */}
      <Section
        open={clickTagsOpen}
        setOpen={setClickTagsOpen}
        icon={<UserCheck size={14} />}
        label="Tag contact on click"
        help={{
          anchor: "ghl-tag",
          body: "When a known contact opens this link, these tags are added to them in your CRM — which can trigger your workflows. The link must carry the contact id (sent from your CRM).",
        }}
        badge={
          clickTags.length > 0 ? (
            <Badge tone="brand" className="ml-1">
              {clickTags.length}
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <label className="flex items-start gap-2.5 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={appendContactId}
            onChange={(e) => setAppendContactId(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-2 focus:ring-brand-500/30"
          />
          <span className="text-xs text-ink-700">
            <span className="font-medium">
              Append contact id to the short link
            </span>
            <span className="block text-[11px] text-ink-500 mt-0.5">
              We add{" "}
              <span className="font-mono text-ink-600">
                {"?contact_id={{contact.id}}"}
              </span>{" "}
              to the copied short URL automatically — your CRM fills it in when you
              send. Needed for tagging and per-contact analytics.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2.5 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={syncContact}
            onChange={(e) => setSyncContact(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-2 focus:ring-brand-500/30"
          />
          <span className="text-xs text-ink-700">
            <span className="font-medium">Update the contact on click</span>
            <span className="block text-[11px] text-ink-500 mt-0.5">
              Each click sets the contact's <span className="font-medium">last
              clicked link / URL / date</span> and <span className="font-medium">total
              clicks</span> custom fields, and adds a timeline note — great for
              smart lists & workflows.
            </span>
          </span>
        </label>
        {selectedCampaign?.click_tags && selectedCampaign.click_tags.length > 0 && (
          <div className="mb-3 rounded-lg bg-ink-50 border border-ink-200/70 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-ink-600 mb-1.5">
              <Megaphone size={12} className="text-brand-500" />
              Inherited from campaign “{selectedCampaign.name}”
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selectedCampaign.click_tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center h-6 px-2 rounded-md bg-white text-ink-600 text-[11px] ring-1 ring-ink-200"
                >
                  {t}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-ink-400 mt-1.5">
              Applied automatically on every click — your tags below are added on
              top.
            </p>
          </div>
        )}
        <p className="text-[11px] text-ink-500 mb-2.5">
          {selectedCampaign?.click_tags?.length
            ? "Extra tags for this link only, applied alongside the campaign tags above."
            : "Tags applied to the contact on each click — ideal for triggering workflows."}
        </p>
        {clickTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {clickTags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-lg bg-brand-50 text-brand-700 text-xs font-medium ring-1 ring-brand-200/60"
              >
                {t}
                <button
                  type="button"
                  onClick={() => removeClickTag(t)}
                  className="w-4 h-4 rounded flex items-center justify-center hover:bg-brand-200/60 text-brand-500"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          type="text"
          value={clickTagInput}
          onChange={(e) => setClickTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addClickTag(clickTagInput);
            } else if (e.key === "Backspace" && !clickTagInput && clickTags.length) {
              removeClickTag(clickTags[clickTags.length - 1]);
            }
          }}
          onBlur={() => addClickTag(clickTagInput)}
          placeholder="Type a tag and press Enter"
          disabled={clickTags.length >= 20}
          className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400 disabled:bg-ink-50"
        />
      </Section>

      {/* Lead capture */}
      <Section
        open={leadOpen}
        setOpen={setLeadOpen}
        icon={<UserPlus size={14} />}
        label="Lead capture"
        help={{
          anchor: "ghl-lead",
          body: "Show a short form before the redirect. The visitor's details are created or updated as a contact in your CRM, then they continue to the destination.",
        }}
        badge={
          leadEnabled ? (
            <Badge tone="brand" className="ml-1">
              On
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <label className="flex items-start gap-2.5 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={leadEnabled}
            onChange={(e) => setLeadEnabled(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-2 focus:ring-brand-500/30"
          />
          <span className="text-xs text-ink-700">
            <span className="font-medium">Ask for details before redirect</span>
            <span className="block text-[11px] text-ink-500 mt-0.5">
              Visitors we can't already identify see a short form first — each
              submission creates/updates a contact in your CRM. Turns anonymous clicks

              into leads.
            </span>
          </span>
        </label>

        {leadEnabled && (
          <div className="space-y-3 pl-1">
            {/* Capture-with toggle: built-in quick form vs a landing page */}
            <div className="inline-flex rounded-lg bg-ink-100 p-0.5 text-xs font-medium">
              {(["form", "page"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setLeadMode(m)}
                  className={`px-3 h-7 rounded-md transition-colors ${
                    leadMode === m
                      ? "bg-white text-ink-900 shadow-sm"
                      : "text-ink-500 hover:text-ink-800"
                  }`}
                >
                  {m === "form" ? "Quick form" : "Landing page"}
                </button>
              ))}
            </div>

            {leadMode === "form" ? (
              <>
                <div>
                  <div className="text-[11px] text-ink-600 mb-1.5">
                    Fields to collect
                  </div>
                  <LeadFieldsEditor
                    fields={leadFields}
                    onChange={setLeadFields}
                    customFields={customFields}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-ink-600 mb-1">
                    Headline
                  </label>
                  <input
                    type="text"
                    value={leadHeadline}
                    onChange={(e) => setLeadHeadline(e.target.value)}
                    maxLength={140}
                    placeholder="Almost there"
                    className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-ink-600 mb-1">
                    Subtext
                  </label>
                  <input
                    type="text"
                    value={leadSubtext}
                    onChange={(e) => setLeadSubtext(e.target.value)}
                    maxLength={300}
                    placeholder="Enter your details to continue."
                    className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-[11px] text-ink-600 mb-1">
                  Landing page to show
                </label>
                <select
                  value={leadPageId}
                  onChange={(e) => setLeadPageId(e.target.value)}
                  className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                >
                  <option value="">Select a page…</option>
                  {leadPages.map((p) => {
                    const hasForm = (p.blocks || []).some((b) => b.type === "form");
                    return (
                      <option key={p._id} value={p._id}>
                        {p.title}
                        {hasForm ? "" : " — no form block"}
                      </option>
                    );
                  })}
                </select>
                <p className="text-[11px] text-ink-400 mt-1.5">
                  The page is shown as the gate; its form submission creates the
                  contact, then the visitor continues to your destination. Add a{" "}
                  <span className="font-medium">Form</span> block to the page, and
                  keep it form-focused so visitors don't wander off.
                </p>
                {!!leadPageId &&
                  !leadPages
                    .find((p) => p._id === leadPageId)
                    ?.blocks?.some((b) => b.type === "form") && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      This page has no form block — it won't capture anything.
                    </p>
                  )}
              </div>
            )}
          </div>
        )}
      </Section>
      </Group>

      {/* Campaign group */}
      <Group title="Campaign" description="Roll this link's clicks into a campaign.">
      {/* Campaign */}
      <Section
        open={campOpen}
        setOpen={setCampOpen}
        icon={<Megaphone size={14} />}
        label="Campaign"
        help={{
          anchor: "campaigns",
          body: "Group this link under a campaign so its clicks roll up with related links. Any campaign-wide tags are also applied on click.",
        }}
        feature="campaigns"
        badge={
          selectedCampaign ? (
            <Badge tone="brand" className="ml-1">
              <span
                className="inline-block w-2 h-2 rounded-full mr-1"
                style={{ background: selectedCampaign.color }}
              />
              {selectedCampaign.name}
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-2.5">
          Add this link to a campaign so its clicks roll up into the campaign's
          analytics view.
        </p>
        {campaigns.length === 0 ? (
          <p className="text-xs text-ink-400">
            No campaigns yet — create one on the Campaigns page first.
          </p>
        ) : (
          <select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          >
            <option value="">No campaign</option>
            {campaigns.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </Section>
      </Group>

      {/* Targeting & routing */}
      <Group title="Targeting & routing" description="Send visitors to different URLs by country, device, or split test.">
      {/* Geo targeting */}
      <Section
        open={geoOpen}
        setOpen={setGeoOpen}
        icon={<Globe size={14} />}
        label="Country targeting"
        help={{
          anchor: "route-country",
          body: "Send visitors from specific countries to different destinations. Country rules are checked first, before device rules and A/B rotation.",
        }}
        feature="geoTargeting"
        badge={
          geoRules.length > 0 ? (
            <Badge tone="brand" className="ml-1">
              {geoRules.length} {geoRules.length === 1 ? "rule" : "rules"}
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-3">
          Route visitors to a different URL based on the country we detect from
          their IP. Add a rule per country — everyone else falls back to your
          default destination.
        </p>

        {/* Rules list */}
        {geoRules.length > 0 ? (
          <div className="space-y-1.5 mb-3">
            {geoRules.map((r) => (
              <div
                key={r.country_code}
                className="group/rule flex items-center gap-2.5 bg-white border border-ink-200 rounded-xl pl-2.5 pr-2 py-2 anim-in"
              >
                <span className="text-lg leading-none shrink-0">
                  {flagEmoji(r.country_code) || "🏳️"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-ink-800 truncate">
                      {r.country || countryName(r.country_code)}
                    </span>
                    <span className="text-[10px] font-mono text-ink-400 uppercase">
                      {r.country_code}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-ink-500 mt-0.5">
                    <CornerDownRight
                      size={11}
                      className="text-brand-400 shrink-0"
                    />
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono truncate hover:text-brand-600 hover:underline"
                    >
                      {r.url}
                    </a>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeGeoRule(r.country_code)}
                  className="w-7 h-7 shrink-0 rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center transition-colors"
                  title="Remove rule"
                >
                  <X size={13} />
                </button>
              </div>
            ))}

            {/* Fallback row — makes the default destination's role explicit. */}
            <div className="flex items-center gap-2.5 rounded-xl border border-dashed border-ink-200 bg-ink-50/40 pl-2.5 pr-3 py-2">
              <span className="text-base leading-none shrink-0">🌍</span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-ink-600">
                  Everyone else
                </div>
                <div className="flex items-center gap-1 text-[11px] text-ink-400 mt-0.5">
                  <CornerDownRight size={11} className="shrink-0" />
                  <span className="font-mono truncate">
                    {url.trim() || "Default destination URL"}
                  </span>
                </div>
              </div>
              <span className="text-[10px] font-medium text-ink-400 uppercase tracking-wide shrink-0">
                Fallback
              </span>
            </div>
          </div>
        ) : (
          <div className="mb-3 rounded-xl border border-dashed border-ink-200 bg-ink-50/40 px-3 py-4 text-center">
            <Globe size={18} className="mx-auto text-ink-300 mb-1.5" />
            <p className="text-[11px] text-ink-400">
              No country rules yet. Add one below to start routing by location.
            </p>
          </div>
        )}

        {/* Add a rule */}
        <div className="rounded-xl border border-ink-200/70 bg-white p-2.5 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-2">
            <CountrySelect
              value={ruleCountry}
              onChange={setRuleCountry}
              usedCodes={geoRules.map((r) => r.country_code)}
            />
            <input
              type="url"
              value={ruleUrl}
              onChange={(e) => setRuleUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addGeoRule();
                }
              }}
              placeholder="https://example.com/landing-for-this-country"
              className="px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400 font-mono"
            />
          </div>
          {ruleCountry && alreadyTargeted && (
            <p className="text-[11px] text-amber-700 flex items-center gap-1">
              <AlertCircle size={11} className="shrink-0" />
              {countryName(ruleCountry)} already has a rule — adding will replace
              it.
            </p>
          )}
          <Button
            type="button"
            variant="gradient"
            size="sm"
            onClick={addGeoRule}
            disabled={!ruleCountry || !ruleUrl.trim()}
            icon={<Plus size={14} strokeWidth={2.5} />}
            className="w-full"
          >
            {alreadyTargeted ? "Update rule" : "Add country rule"}
          </Button>
        </div>
      </Section>

      {/* Device targeting */}
      <Section
        open={deviceOpen}
        setOpen={setDeviceOpen}
        icon={<Smartphone size={14} />}
        label="Device targeting"
        help={{
          anchor: "route-device",
          body: "Send visitors on specific devices or operating systems (iOS, Android, desktop and so on) to different destinations. Checked after country rules.",
        }}
        feature="deviceTargeting"
        badge={
          deviceRules.length > 0 ? (
            <Badge tone="brand" className="ml-1">
              {deviceRules.length} {deviceRules.length === 1 ? "rule" : "rules"}
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-3">
          Send visitors to a different URL based on their device or OS — e.g. iOS → App
          Store, Android → Play Store. Checked after country rules; anyone unmatched falls
          back to the destination URL.
        </p>
        {deviceRules.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {deviceRules.map((r) => (
              <div
                key={r.match}
                className="flex items-center gap-2.5 bg-white border border-ink-200 rounded-xl pl-3 pr-2 py-2"
              >
                <Smartphone size={14} className="text-brand-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-ink-800">{deviceLabel(r.match)}</div>
                  <div className="flex items-center gap-1 text-[11px] text-ink-500 mt-0.5">
                    <CornerDownRight size={11} className="text-brand-400 shrink-0" />
                    <span className="font-mono truncate">{r.url}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeDeviceRule(r.match)}
                  className="w-7 h-7 shrink-0 rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="rounded-xl border border-ink-200/70 bg-white p-2.5 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-2">
            <select
              value={deviceMatch}
              onChange={(e) => setDeviceMatch(e.target.value as DeviceMatch)}
              className="px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            >
              {DEVICE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <input
              type="url"
              value={deviceUrl}
              onChange={(e) => setDeviceUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addDeviceRule();
                }
              }}
              placeholder="https://apps.apple.com/..."
              className="px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400 font-mono"
            />
          </div>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            onClick={addDeviceRule}
            disabled={!deviceUrl.trim()}
            icon={<Plus size={14} strokeWidth={2.5} />}
            className="w-full"
          >
            {deviceRules.some((r) => r.match === deviceMatch) ? "Update rule" : "Add device rule"}
          </Button>
        </div>
      </Section>

      {/* A/B rotation */}
      <Section
        open={rotationOpen}
        setOpen={setRotationOpen}
        icon={<Shuffle size={14} />}
        label="A/B rotation"
        help={{
          anchor: "route-ab",
          body: "Split clicks across multiple destinations by weight to test what converts. Used when no country or device rule matches the visitor.",
        }}
        feature="abTesting"
        badge={
          rotation.length > 0 ? (
            <Badge tone="brand" className="ml-1">
              {rotation.length} {rotation.length === 1 ? "variant" : "variants"}
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-3">
          Split clicks across multiple destinations by weight. Country and device rules still
          take priority; everyone else is shared out here. Compare results by destination in
          Analytics.
        </p>
        {rotation.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {rotation.map((r, i) => {
              const pct = rotationTotal > 0 ? Math.round(((r.weight > 0 ? r.weight : 1) / rotationTotal) * 100) : 0;
              return (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-white border border-ink-200 rounded-xl pl-2.5 pr-2 py-2"
                >
                  <input
                    type="url"
                    value={r.url}
                    onChange={(e) => updateRotation(i, { url: e.target.value })}
                    placeholder="https://example.com/variant"
                    className="flex-1 min-w-0 px-2 h-8 bg-transparent text-sm focus:outline-none placeholder:text-ink-400 font-mono"
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={r.weight}
                      onChange={(e) =>
                        updateRotation(i, { weight: Math.max(1, parseInt(e.target.value, 10) || 1) })
                      }
                      className="w-14 px-2 h-8 bg-white border border-ink-200 rounded-md text-xs text-center focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                      title="Weight"
                    />
                    <span className="text-[11px] text-ink-400 tabular-nums w-9 text-right">{pct}%</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRotation(i)}
                    className="w-7 h-7 shrink-0 rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addRotation}
          disabled={rotation.length >= 10}
          icon={<Plus size={14} strokeWidth={2.5} />}
          className="w-full"
        >
          Add a variant
        </Button>
      </Section>

      {/* Deep link — open in the native app */}
      <label className="flex items-start gap-2.5 px-3.5 py-3 border border-ink-200/70 rounded-xl bg-white cursor-pointer hover:bg-ink-50/40">
        <input
          type="checkbox"
          checked={deepLink}
          onChange={(e) => setDeepLink(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500/30"
        />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-medium text-ink-700">
            <Smartphone size={13} className="text-ink-500" />
            Open in native app
            <HelpHint title="Open in native app" anchor="field-deeplink">
              On mobile, the link tries to open the destination in its installed
              app (YouTube, Instagram, TikTok, Spotify, X, Facebook, LinkedIn),
              falling back to the browser if the app isn't there.
            </HelpHint>
          </span>
          <span className="block text-[11px] text-ink-500 mt-0.5">
            Tries to open supported destinations in the app instead of the
            browser.
          </span>
        </span>
      </label>
      </Group>

      {/* Security */}
      <Group title="Security" description="Require a password before redirecting.">
      {/* Password */}
      <Section
        open={pwOpen}
        setOpen={setPwOpen}
        icon={<Lock size={14} />}
        label="Password protection"
        help={{
          anchor: "protect-password",
          body: "Require a password before the link redirects. Visitors see an unlock page first and must enter the password to continue.",
        }}
        feature="password"
        badge={
          initial?.has_password && !clearPassword ? (
            <Badge tone="emerald" className="ml-1">
              Active
            </Badge>
          ) : password ? (
            <Badge tone="brand" className="ml-1">
              New
            </Badge>
          ) : (
            <span className="text-[11px] text-ink-400 ml-1">Optional</span>
          )
        }
      >
        <p className="text-[11px] text-ink-500 mb-2.5">
          Visitors will see a password gate before being redirected.
        </p>
        {initial?.has_password && !clearPassword && !password && (
          <div className="mb-2.5 flex items-center justify-between bg-emerald-50/60 border border-emerald-200 rounded-lg px-3 py-2 text-xs">
            <span className="text-emerald-800">
              This link already has a password set.
            </span>
            <button
              type="button"
              onClick={() => setClearPassword(true)}
              className="text-emerald-700 hover:text-emerald-900 font-medium underline-offset-2 hover:underline"
            >
              Remove
            </button>
          </div>
        )}
        {clearPassword && (
          <div className="mb-2.5 flex items-center justify-between bg-rose-50/60 border border-rose-200 rounded-lg px-3 py-2 text-xs">
            <span className="text-rose-800">
              Password will be removed on save.
            </span>
            <button
              type="button"
              onClick={() => setClearPassword(false)}
              className="text-rose-700 hover:text-rose-900 font-medium underline-offset-2 hover:underline"
            >
              Undo
            </button>
          </div>
        )}
        <div className="relative">
          <Lock
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (e.target.value) setClearPassword(false);
            }}
            placeholder={
              initial?.has_password
                ? "Enter to change password"
                : "Set a password"
            }
            className="w-full pl-9 pr-10 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400 font-mono"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700 p-1"
          >
            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </Section>
      </Group>

      {error && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="gradient"
          loading={submitting}
          disabled={slugBlocksSubmit || destBlocksSubmit}
          title={
            slugBlocksSubmit
              ? "Pick an available custom short link first"
              : destBlocksSubmit
              ? "Pick a funnel/website page, or switch to a custom URL"
              : undefined
          }
          icon={!submitting ? <Sparkles size={14} /> : undefined}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default LinkForm;
